import React from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import TifpaLogo from './ui/TifpaLogo';
import {
  LayoutDashboard,
  LogOut,
  X,
  FileSpreadsheet,
  Sparkles,
  ChevronDown,
  ChevronRight,
  MousePointerClick,
  Maximize2,
  Minimize2,
  Settings,
  Mail,
  Users,
  Building2,
  CalendarClock,
  Heart,
  Receipt,
  Calendar1,
} from 'lucide-react';
import { Separator } from '@radix-ui/react-separator';
import { UserAvatar } from './UserAvatar';

interface NavigationItem {
  name: string;
  href: string;
  icon: React.ComponentType<any>;
  submenu?: NavigationItem[];
  canAccess?: (employeeRole: string | null) => boolean;
}

type SidebarMode = 'hover' | 'expanded' | 'collapsed';

// Check if user can access Email Management (Admin+ since 2026-08-28)
const canAccessEmail = (employeeRole: string | null): boolean => {
  return employeeRole === 'SuperAdmin' || employeeRole === 'Admin';
};

// Shared Manager+ gate (SuperAdmin, Admin, Manager only)
const isManagerPlus = (employeeRole: string | null): boolean => {
  return (
    employeeRole === 'SuperAdmin' ||
    employeeRole === 'Admin' ||
    employeeRole === 'Manager'
  );
};

// Check if user can access ACH Payors (SuperAdmin, Admin, Manager only)
const canAccessACHPayors = isManagerPlus;

// New V2 builtin pages (1099 & LLC Filing, Extensions, BK Not EFiled)
// temporarily SuperAdmin-only while testing (2026-08-11); the page-level
// guards in each page match. Widen back to isManagerPlus after rollout.
const canAccessV2BuiltinPages = (employeeRole: string | null): boolean => {
  return employeeRole === 'SuperAdmin';
};

interface NavigationGroup {
  label: string;
  items: NavigationItem[];
}

const navigationGroups: NavigationGroup[] = [
  {
    label: 'Work',
    items: [
      { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
      {
        name: 'TIFPA Workflow',
        href: '/workflow-v2',
        icon: Sparkles,
      },
      {
        name: 'Bank Data',
        href: '/bank-data-v2',
        icon: Building2,
      },
      {
        name: 'Sales Taxes',
        href: '/sales-tax-v2',
        icon: Receipt,
      },
      // Temporarily SuperAdmin-only while testing; the server-side guards
      // (sql/spreadsheets/002-004) allow Manager+ on these sheet families
      // {
      //   name: '1099 & LLC Filing',
      //   href: '/llc-filing-v2',
      //   icon: FileText,
      //   canAccess: canAccessV2BuiltinPages,
      // },
      // {
      //   name: 'Extensions',
      //   href: '/extensions-v2',
      //   icon: FileClock,
      //   canAccess: canAccessV2BuiltinPages,
      // },
      // {
      //   name: 'BK Not EFiled',
      //   href: '/bookkeeping-not-efiled-v2',
      //   icon: BookOpenCheck,
      //   canAccess: canAccessV2BuiltinPages,
      // },
    ],
  },
  {
    label: 'Admin',
    items: [
      { name: 'Clients', href: '/clients', icon: Users },
      {
        name: 'Email',
        href: '/email',
        icon: Mail,
        canAccess: canAccessEmail,
      },
      // {
      //   name: 'ACH Payors',
      //   href: '/ach-payors',
      //   icon: HandCoins,
      //   canAccess: canAccessACHPayors,
      // },
      {
        name: 'Appointments',
        href: '/appointments',
        icon: CalendarClock,
      },
    ],
  },
  {
    label: 'Resources',
    items: [
      { name: 'Favorite Links', href: '/favorite-links', icon: Heart },
      // All roles (temporary while Workflow V2 adoption settles): RLS limits
      // sub-Manager users to the workflow/annual-sales-tax/incoming families
      // { name: 'Spreadsheets', href: '/spreadsheets', icon: FileSpreadsheet },
      { name: 'Calendar', href: '/calendar', icon: Calendar1 },
    ],
  },
];

// Flat list kept for the mobile sidebar, which doesn't show groups
const navigation: NavigationItem[] = navigationGroups.flatMap(g => g.items);

export default function Layout() {
  const { user, profile, employeeRole, signOut } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = React.useState(false);
  const [sidebarMode, setSidebarMode] = React.useState<SidebarMode>(() => {
    // Load sidebar mode from localStorage
    const savedMode = localStorage.getItem('sidebarMode');
    return (savedMode as SidebarMode) || 'hover';
  });
  const [sidebarExpanded, setSidebarExpanded] = React.useState(false);
  const [contextMenuPosition, setContextMenuPosition] = React.useState<{
    x: number;
    y: number;
  } | null>(null);
  // const [pinnedSpreadsheets, setPinnedSpreadsheets] = React.useState<any[]>([]);

  // Ref to prevent double-loading pinned spreadsheets in React Strict Mode
  // const pinnedLoadedRef = React.useRef(false);
  // const [showPinnedSpreadsheets, setShowPinnedSpreadsheets] = React.useState(
  //   () => {
  //     // Load pinned spreadsheets visibility from localStorage
  //     const savedVisibility = localStorage.getItem('showPinnedSpreadsheets');
  //     return savedVisibility !== null ? savedVisibility === 'true' : true;
  //   }
  // );
  const [expandedMenus, setExpandedMenus] = React.useState<{
    [key: string]: boolean;
  }>({
    'Bank Downloads': true, // Always expand Bank Downloads
    Email: true, // Always expand Email
  });
  const [showAvatarSettings, setShowAvatarSettings] = React.useState(false);
  const collapseTimer = React.useRef<NodeJS.Timeout | null>(null);

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  const toggleSubmenu = (menuName: string) => {
    // Don't allow toggling Bank Downloads - it should always be expanded
    if (menuName === 'Bank Downloads') return;

    setExpandedMenus(prev => ({
      ...prev,
      [menuName]: !prev[menuName],
    }));
  };

  const handleSidebarMouseEnter = () => {
    // Clear any existing timer
    if (collapseTimer.current) {
      clearTimeout(collapseTimer.current);
      collapseTimer.current = null;
    }
    // Only expand on hover if in 'hover' mode
    if (sidebarMode === 'hover') {
      setSidebarExpanded(true);
    }
  };

  const handleSidebarMouseLeave = () => {
    // Only collapse if in 'hover' mode
    if (sidebarMode === 'hover') {
      // Set timer to collapse after 500ms
      collapseTimer.current = setTimeout(() => {
        setSidebarExpanded(false);
      }, 500);
    }
  };

  const setSidebarModeAndExpand = (mode: SidebarMode) => {
    setSidebarMode(mode);
    if (mode === 'expanded') {
      setSidebarExpanded(true);
    } else if (mode === 'collapsed') {
      setSidebarExpanded(false);
    }
    setContextMenuPosition(null);
  };

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    setContextMenuPosition({ x: e.clientX, y: e.clientY });
  };

  // Update expansion state based on mode
  React.useEffect(() => {
    if (sidebarMode === 'expanded') {
      setSidebarExpanded(true);
    } else if (sidebarMode === 'collapsed') {
      setSidebarExpanded(false);
    }
  }, [sidebarMode]);

  // Save sidebar mode to localStorage whenever it changes
  React.useEffect(() => {
    localStorage.setItem('sidebarMode', sidebarMode);
  }, [sidebarMode]);

  // Save pinned spreadsheets visibility to localStorage whenever it changes
  // React.useEffect(() => {
  //   localStorage.setItem(
  //     'showPinnedSpreadsheets',
  //     String(showPinnedSpreadsheets)
  //   );
  // }, [showPinnedSpreadsheets]);

  // Load pinned spreadsheets
  // React.useEffect(() => {
  //   // Prevent double-loading in React Strict Mode
  //   if (pinnedLoadedRef.current || !user) {
  //     return;
  //   }
  //   pinnedLoadedRef.current = true;

  //   const loadPinnedSpreadsheets = async () => {
  //     try {
  //       const pinned = await getPinnedSpreadsheets();
  //       setPinnedSpreadsheets(pinned);
  //     } catch (error) {
  //       console.error('Error loading pinned spreadsheets:', error);
  //     }
  //   };

  //   loadPinnedSpreadsheets();
  // }, [user]);

  // Cleanup timer on unmount
  React.useEffect(() => {
    return () => {
      if (collapseTimer.current) {
        clearTimeout(collapseTimer.current);
      }
    };
  }, []);

  const isCurrentPath = (path: string) => {
    return (
      location.pathname === path || location.pathname.startsWith(`${path}/`)
    );
  };

  return (
    <>
      <div className="h-screen flex overflow-hidden bg-slate-50">
        {/* Mobile sidebar overlay */}
        {sidebarOpen && (
          <div className="fixed inset-0 flex z-40 md:hidden">
            <div
              className="fixed inset-0 bg-slate-900/70 backdrop-blur-md transition-all duration-300"
              onClick={() => setSidebarOpen(false)}
            />
            <div className="relative flex-1 flex flex-col max-w-xs w-full bg-white shadow-2xl border-r border-slate-100">
              <div className="absolute top-0 right-0 -mr-12 pt-2">
                <button
                  className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white hover:bg-white/20 transition-all duration-200 hover:scale-105 active:scale-95"
                  onClick={() => setSidebarOpen(false)}
                >
                  <X className="h-6 w-6 text-white drop-shadow-md" />
                </button>
              </div>
              <MobileSidebarContent />
            </div>
          </div>
        )}

        {/* Static sidebar for desktop */}
        <div className="hidden md:flex md:flex-shrink-0">
          <div
            className={`flex flex-col transition-all duration-300 ease-in-out shadow-xl border-r border-slate-100/60 ${
              sidebarExpanded ? 'w-56' : 'w-16'
            }`}
            onMouseEnter={handleSidebarMouseEnter}
            onMouseLeave={handleSidebarMouseLeave}
            onContextMenu={handleContextMenu}
          >
            <SidebarContent />
          </div>
        </div>

        {/* Main content */}
        <div className="flex flex-col w-0 flex-1 overflow-hidden">
          {/* Top navigation - Mobile only */}
          <div className="relative z-10 flex-shrink-0 flex h-16 bg-white shadow md:hidden">
            <button
              className="px-4 border-r border-slate-200 text-slate-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-tifpa-rose"
              onClick={() => setSidebarOpen(true)}
            >
              <svg
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
            </button>
            <div className="flex-1 flex items-center justify-center">
              <TifpaLogo className="h-6 w-auto" />
            </div>
          </div>

          {/* Page content */}
          <main className="flex-1 relative overflow-y-auto focus:outline-none">
            {/* Check if we're on the spreadsheet view for full-width layout without padding */}
            {location.pathname.startsWith('/spreadsheets') ? (
              <Outlet />
            ) : (
              // <div className="py-6">
              //   <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
              <Outlet />
              //   </div>
              // </div>
            )}
          </main>
        </div>
      </div>

      {/* Context Menu */}
      {contextMenuPosition && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setContextMenuPosition(null)}
          />
          <div
            className="fixed z-50 bg-white rounded-lg shadow-xl border border-slate-200 py-1 min-w-[180px]"
            style={{
              left: `${contextMenuPosition.x}px`,
              top: `${contextMenuPosition.y}px`,
            }}
          >
            <button
              onClick={() => setSidebarModeAndExpand('hover')}
              className="w-full px-4 py-2 text-left text-sm hover:bg-slate-50 flex items-center gap-3"
            >
              <MousePointerClick
                className={`h-4 w-4 ${sidebarMode === 'hover' ? 'text-tifpa-rose' : 'text-slate-400'}`}
              />
              <span
                className={
                  sidebarMode === 'hover' ? 'font-semibold text-tifpa-rose' : ''
                }
              >
                Expand on hover
              </span>
            </button>
            <button
              onClick={() => setSidebarModeAndExpand('expanded')}
              className="w-full px-4 py-2 text-left text-sm hover:bg-slate-50 flex items-center gap-3"
            >
              <Maximize2
                className={`h-4 w-4 ${sidebarMode === 'expanded' ? 'text-tifpa-rose' : 'text-slate-400'}`}
              />
              <span
                className={
                  sidebarMode === 'expanded'
                    ? 'font-semibold text-tifpa-rose'
                    : ''
                }
              >
                Always expanded
              </span>
            </button>
            <button
              onClick={() => setSidebarModeAndExpand('collapsed')}
              className="w-full px-4 py-2 text-left text-sm hover:bg-slate-50 flex items-center gap-3"
            >
              <Minimize2
                className={`h-4 w-4 ${sidebarMode === 'collapsed' ? 'text-tifpa-rose' : 'text-slate-400'}`}
              />
              <span
                className={
                  sidebarMode === 'collapsed'
                    ? 'font-semibold text-tifpa-rose'
                    : ''
                }
              >
                Always collapsed
              </span>
            </button>
          </div>
        </>
      )}
    </>
  );

  function MobileSidebarContent() {
    // Filter navigation to only show Dashboard and Appointments
    const mobileNavigation = [
      navigation.find(item => item.name === 'Dashboard'),
      navigation.find(item => item.name === 'Appointments V2'),
    ].filter(Boolean) as NavigationItem[];

    return (
      <div className="flex flex-col h-full bg-white">
        <div className="flex-1 flex flex-col pt-6 pb-4 overflow-y-auto">
          <div className="flex items-center justify-center flex-shrink-0 px-4 mb-6">
            <TifpaLogo className="h-8 w-auto" />
          </div>

          <nav className="flex-1 px-4 space-y-2">
            {mobileNavigation.map(item => {
              const current = isCurrentPath(item.href);
              const hasSubmenu = item.submenu && item.submenu.length > 0;

              return (
                <div key={item.name}>
                  {/* Main navigation item */}
                  <Link
                    to={item.href}
                    onClick={() => setSidebarOpen(false)}
                    className={`
                      group flex items-center px-4 py-3 text-base font-medium rounded-lg transition-all duration-200
                      ${
                        current
                          ? 'bg-gradient-to-r from-pink-100 via-pink-50 to-pink-100 text-tifpa-rose shadow-sm ring-1 ring-pink-200'
                          : 'text-slate-700 hover:bg-gradient-to-r hover:from-slate-50 hover:via-slate-100 hover:to-slate-50 hover:text-tifpa-rose'
                      }
                    `}
                  >
                    <item.icon className="flex-shrink-0 h-6 w-6 mr-4" />
                    <span className="font-medium">{item.name}</span>
                  </Link>

                  {/* Submenu items */}
                  {hasSubmenu && item.submenu && (
                    <div className="mt-2 ml-6 space-y-1 border-l-2 border-slate-200 pl-4">
                      {item.submenu.map(subItem => {
                        const subCurrent = isCurrentPath(subItem.href);
                        return (
                          <Link
                            key={subItem.name}
                            to={subItem.href}
                            onClick={() => setSidebarOpen(false)}
                            className={`
                              group flex items-center px-3 py-2.5 text-sm rounded-lg transition-all duration-200
                              ${
                                subCurrent
                                  ? 'bg-gradient-to-r from-pink-100 via-pink-50 to-pink-100 text-tifpa-rose shadow-sm ring-1 ring-pink-200'
                                  : 'text-slate-600 hover:text-tifpa-rose hover:bg-gradient-to-r hover:from-slate-50 hover:via-slate-100 hover:to-slate-50'
                              }
                            `}
                          >
                            <subItem.icon className="flex-shrink-0 h-5 w-5 mr-3" />
                            <span>{subItem.name}</span>
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}

            {/* TIFPA Workflow V2 - in-portal rebuild */}
            <Link
              to="/workflow-v2"
              onClick={() => setSidebarOpen(false)}
              className={`group flex items-center px-4 py-3 text-base font-medium rounded-lg transition-all duration-200 ${
                isCurrentPath('/workflow-v2')
                  ? 'bg-gradient-to-r from-pink-100 via-pink-50 to-pink-100 text-tifpa-rose shadow-sm ring-1 ring-pink-200'
                  : 'text-slate-700 hover:bg-gradient-to-r hover:from-slate-50 hover:via-slate-100 hover:to-slate-50 hover:text-tifpa-rose'
              }`}
            >
              <FileSpreadsheet className="flex-shrink-0 h-6 w-6 mr-4" />
              <span className="font-medium flex-1">TIFPA Workflow V2</span>
            </Link>

            {/* Bank Data V2 - in-portal rebuild */}
            <Link
              to="/bank-data-v2"
              onClick={() => setSidebarOpen(false)}
              className={`group flex items-center px-4 py-3 text-base font-medium rounded-lg transition-all duration-200 ${
                isCurrentPath('/bank-data-v2')
                  ? 'bg-gradient-to-r from-pink-100 via-pink-50 to-pink-100 text-tifpa-rose shadow-sm ring-1 ring-pink-200'
                  : 'text-slate-700 hover:bg-gradient-to-r hover:from-slate-50 hover:via-slate-100 hover:to-slate-50 hover:text-tifpa-rose'
              }`}
            >
              <Building2 className="flex-shrink-0 h-6 w-6 mr-4" />
              <span className="font-medium flex-1">Bank Data V2</span>
            </Link>

            {/* Sales Tax V2 - in-portal rebuild */}
            <Link
              to="/sales-tax-v2"
              onClick={() => setSidebarOpen(false)}
              className={`group flex items-center px-4 py-3 text-base font-medium rounded-lg transition-all duration-200 ${
                isCurrentPath('/sales-tax-v2')
                  ? 'bg-gradient-to-r from-pink-100 via-pink-50 to-pink-100 text-tifpa-rose shadow-sm ring-1 ring-pink-200'
                  : 'text-slate-700 hover:bg-gradient-to-r hover:from-slate-50 hover:via-slate-100 hover:to-slate-50 hover:text-tifpa-rose'
              }`}
            >
              <Receipt className="flex-shrink-0 h-6 w-6 mr-4" />
              <span className="font-medium flex-1">Sales Taxes V2</span>
            </Link>

            {/* Email - Conditional Navigation Item (Admin+) */}
            {canAccessEmail(employeeRole) && (
              <Link
                to="/email"
                onClick={() => setSidebarOpen(false)}
                className={`
                  group flex items-center px-4 py-3 text-base font-medium rounded-lg transition-all duration-200
                  ${
                    isCurrentPath('/email')
                      ? 'bg-gradient-to-r from-pink-100 via-pink-50 to-pink-100 text-tifpa-rose shadow-sm ring-1 ring-pink-200'
                      : 'text-slate-700 hover:bg-gradient-to-r hover:from-slate-50 hover:via-slate-100 hover:to-slate-50 hover:text-tifpa-rose'
                  }
                `}
              >
                <Mail className="flex-shrink-0 h-6 w-6 mr-4" />
                <span className="font-medium">Email</span>
              </Link>
            )}

            {/* ACH Payors V2 - Conditional Navigation Item (Only for SuperAdmin, Admin, Manager) */}
            {/* {canAccessACHPayors(employeeRole) && (
              <Link
                to="/ach-payors"
                onClick={() => setSidebarOpen(false)}
                className={`group flex items-center px-4 py-3 text-base font-medium rounded-lg transition-all duration-200 ${
                  isCurrentPath('/ach-payors')
                    ? 'bg-gradient-to-r from-pink-100 via-pink-50 to-pink-100 text-tifpa-rose shadow-sm ring-1 ring-pink-200'
                    : 'text-slate-700 hover:bg-gradient-to-r hover:from-slate-50 hover:via-slate-100 hover:to-slate-50 hover:text-tifpa-rose'
                }`}
              >
                <HandCoins className="flex-shrink-0 h-6 w-6 mr-4" />
                <span className="font-medium flex-1">ACH Payors V2</span>
              </Link>
            )} */}
          </nav>

          <Separator className="bg-gradient-to-r from-transparent via-slate-200 to-transparent h-px my-4" />

          <div className="px-4 space-y-4 pb-2">
            {/* User info */}
            <div
              className="flex items-center space-x-3 px-3 py-3 rounded-lg hover:bg-slate-50 cursor-pointer transition-all duration-200"
              onClick={() => {
                navigate('/settings');
                setSidebarOpen(false);
              }}
            >
              <UserAvatar
                avatarUrl={profile?.avatar_url}
                avatarColor={profile?.avatar_color}
                name={
                  profile?.display_name ||
                  profile?.name ||
                  user?.email ||
                  undefined
                }
                size="sm"
                className="shadow-lg ring-2 ring-white"
              />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-slate-900 truncate">
                  {profile?.display_name ||
                    profile?.name ||
                    user?.email?.replace('@tifpaconsulting.com', '')}
                </p>
                {employeeRole && (
                  <p className="text-xs text-slate-500 font-medium">
                    {employeeRole === 'SuperAdmin' ? 'Admin' : employeeRole}
                  </p>
                )}
              </div>
            </div>

            {/* Sign out button */}
            <Button
              variant="outline"
              size="sm"
              onClick={handleSignOut}
              className="w-full flex items-center justify-center space-x-2 hover:bg-red-50 hover:text-red-600 hover:border-red-200 transition-all duration-200"
            >
              <LogOut className="h-4 w-4" />
              <span>Sign Out</span>
            </Button>
          </div>
        </div>
      </div>
    );
  }

  function SidebarContent() {
    return (
      <div className="flex flex-col h-0 flex-1 bg-white">
        <div className="flex-1 flex flex-col pt-6 pb-4 overflow-y-auto">
          <div
            className={`flex items-center flex-shrink-0 transition-all duration-300 mb-2 ${
              sidebarExpanded ? 'px-4' : 'px-2 justify-center'
            }`}
          >
            <TifpaLogo
              className={`w-auto transition-all duration-300 ${
                sidebarExpanded ? 'h-8' : 'h-6'
              }`}
            />
          </div>

          <nav className="mt-8 flex-1 px-2 space-y-1.5">
            {navigationGroups.map((group, groupIndex) => {
              const items = group.items.filter(
                item => !item.canAccess || item.canAccess(employeeRole)
              );
              if (items.length === 0) return null;

              return (
                <div key={group.label}>
                  {sidebarExpanded ? (
                    <div
                      className={`px-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400 ${
                        groupIndex === 0 ? '' : 'pt-4'
                      }`}
                    >
                      {group.label}
                    </div>
                  ) : (
                    groupIndex > 0 && (
                      <div className="mx-3 my-3 border-t border-slate-200/70" />
                    )
                  )}
                  <div className="space-y-1.5">
                    {items.map(item => {
                      const current = isCurrentPath(item.href);
                      const hasSubmenu =
                        item.submenu && item.submenu.length > 0;
                      const isExpanded = expandedMenus[item.name];

                      return (
                        <div key={item.name}>
                          {/* Main navigation item */}
                          <Link
                            to={item.href}
                            className={`
                      group flex items-center py-2.5 text-sm font-medium rounded-lg transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] relative
                      ${sidebarExpanded ? 'px-3' : 'justify-center px-3'}
                      ${
                        current
                          ? 'bg-gradient-to-r from-pink-100 via-pink-50 to-pink-100 text-tifpa-rose shadow-sm ring-1 ring-pink-200'
                          : 'text-slate-700 hover:bg-gradient-to-r hover:from-slate-50 hover:via-slate-100 hover:to-slate-50 hover:text-tifpa-rose hover:shadow-sm'
                      }
                    `}
                            title={!sidebarExpanded ? item.name : undefined}
                          >
                            {/* Active indicator for collapsed sidebar */}
                            {current && !sidebarExpanded && (
                              <div className="absolute -left-1 top-1/2 -translate-y-1/2 w-1 h-6 bg-tifpa-rose rounded-r-full" />
                            )}

                            <item.icon
                              className={`
                        flex-shrink-0 h-5 w-5 transition-all duration-200
                        ${sidebarExpanded ? 'mr-3' : ''}
                        ${current ? 'scale-110 drop-shadow-sm' : 'group-hover:scale-110 group-hover:drop-shadow-sm'}
                      `}
                            />
                            {sidebarExpanded && (
                              <span className="transition-opacity duration-300 opacity-100 font-medium flex-1">
                                {item.name}
                              </span>
                            )}

                            {/* Submenu toggle button - hide for Bank Downloads */}
                            {hasSubmenu &&
                              sidebarExpanded &&
                              item.name !== 'Bank Downloads' && (
                                <button
                                  onClick={e => {
                                    e.preventDefault();
                                    toggleSubmenu(item.name);
                                  }}
                                  className="p-1 hover:bg-slate-200/50 rounded transition-colors"
                                >
                                  {isExpanded ? (
                                    <ChevronDown className="h-4 w-4" />
                                  ) : (
                                    <ChevronRight className="h-4 w-4" />
                                  )}
                                </button>
                              )}
                          </Link>

                          {/* Submenu items - Expanded sidebar */}
                          {hasSubmenu &&
                            isExpanded &&
                            sidebarExpanded &&
                            item.submenu && (
                              <div className="mt-1 ml-3 space-y-1 border-l-2 border-slate-200 pl-3">
                                {item.submenu.map(subItem => {
                                  const subCurrent = isCurrentPath(
                                    subItem.href
                                  );
                                  return (
                                    <Link
                                      key={subItem.name}
                                      to={subItem.href}
                                      className={`
                              group flex items-center px-3 py-2 text-sm rounded-lg transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]
                              ${
                                subCurrent
                                  ? 'bg-gradient-to-r from-pink-100 via-pink-50 to-pink-100 text-tifpa-rose shadow-sm ring-1 ring-pink-200'
                                  : 'text-slate-600 hover:text-tifpa-rose hover:bg-gradient-to-r hover:from-slate-50 hover:via-slate-100 hover:to-slate-50 hover:shadow-sm'
                              }
                            `}
                                    >
                                      <subItem.icon
                                        className={`flex-shrink-0 h-4 w-4 mr-2 ${subCurrent ? 'scale-110 drop-shadow-sm' : 'group-hover:scale-110'} transition-transform`}
                                      />
                                      <span className="truncate">
                                        {subItem.name}
                                      </span>
                                    </Link>
                                  );
                                })}
                              </div>
                            )}

                          {/* Submenu items - Collapsed sidebar (icons only) */}
                          {hasSubmenu && !sidebarExpanded && item.submenu && (
                            <div className="mt-1 mb-2 space-y-0.5 pl-1 relative">
                              {/* Visual indicator that these are child items */}
                              <div className="absolute left-0 top-1 bottom-1 w-0.5 bg-gradient-to-b from-slate-200 via-slate-300 to-slate-200 rounded-full" />

                              {item.submenu.map(subItem => {
                                const subCurrent = isCurrentPath(subItem.href);
                                return (
                                  <Link
                                    key={subItem.name}
                                    to={subItem.href}
                                    className={`
                                group flex items-center justify-center py-1.5 px-2 rounded-md transition-all duration-200 hover:scale-105 active:scale-95 relative ml-1.5
                                ${
                                  subCurrent
                                    ? 'bg-gradient-to-r from-pink-100 via-pink-50 to-pink-100 text-tifpa-rose shadow-sm ring-1 ring-pink-200'
                                    : 'text-slate-500 hover:text-tifpa-rose hover:bg-gradient-to-r hover:from-slate-50 hover:via-slate-100 hover:to-slate-50 hover:shadow-sm'
                                }
                              `}
                                    title={subItem.name}
                                  >
                                    <subItem.icon
                                      className={`h-3.5 w-3.5 transition-all duration-200 ${subCurrent ? 'scale-110 drop-shadow-sm' : 'group-hover:scale-110'}`}
                                    />
                                    {/* Active indicator - smaller for child items */}
                                    {subCurrent && (
                                      <div className="absolute -left-0.5 top-1/2 -translate-y-1/2 w-0.5 h-3 bg-tifpa-rose rounded-r-full" />
                                    )}
                                  </Link>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </nav>
          <Separator className="bg-gradient-to-r from-transparent via-slate-200 to-transparent h-px my-4" />
          <div className="px-3 space-y-4 pb-2">
            {/* Security Alert - Logged in as */}
            {sidebarExpanded && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-xs text-blue-800 leading-relaxed">
                  <strong>
                    You're logged in as{' '}
                    {profile?.name || user?.email?.split('@')[0]}.
                  </strong>
                  <br />
                  Not you?{' '}
                  <button
                    onClick={handleSignOut}
                    className="underline hover:text-blue-900 font-semibold"
                  >
                    Sign out immediately
                  </button>
                  .
                </p>
              </div>
            )}

            {/* User info with Settings */}
            <div className="relative">
              <div
                className={`flex items-center cursor-pointer group ${sidebarExpanded ? 'space-x-3 px-2 py-2 rounded-lg hover:bg-gradient-to-r hover:from-slate-50 hover:to-transparent transition-all duration-200 hover:shadow-sm' : 'justify-center'}`}
                onClick={() => navigate('/settings')}
                onMouseEnter={() => setShowAvatarSettings(true)}
                onMouseLeave={() => setShowAvatarSettings(false)}
                title="Click to go to Settings"
              >
                <div className="relative">
                  <div className="group-hover:scale-105 transition-all duration-200">
                    <UserAvatar
                      avatarUrl={profile?.avatar_url}
                      avatarColor={profile?.avatar_color}
                      name={
                        profile?.display_name ||
                        profile?.name ||
                        user?.email ||
                        undefined
                      }
                      size="sm"
                      className="shadow-lg ring-2 ring-white group-hover:ring-pink-100"
                    />
                  </div>

                  {/* Settings icon - shows on hover, overlays the avatar */}
                  {showAvatarSettings && (
                    <div className="absolute inset-0 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm rounded-full transition-all duration-200">
                      <Settings className="h-4 w-4 text-white drop-shadow-lg" />
                    </div>
                  )}
                </div>

                {sidebarExpanded && (
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-900 truncate">
                      {profile?.display_name ||
                        profile?.name ||
                        user?.email?.replace('@tifpaconsulting.com', '')}
                    </p>
                    {employeeRole && (
                      <p className="text-xs text-slate-500 font-medium">
                        {employeeRole === 'SuperAdmin' ? 'Admin' : employeeRole}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Sign out button */}
            <div
              className={`flex ${sidebarExpanded ? 'justify-start' : 'justify-center'}`}
            >
              <Button
                variant="outline"
                size="sm"
                onClick={handleSignOut}
                className="flex items-center space-x-2 hover:bg-red-50 hover:text-red-600 hover:border-red-200 transition-all duration-200 hover:shadow-md hover:scale-105 active:scale-95"
                title={!sidebarExpanded ? 'Sign Out' : undefined}
              >
                <LogOut className="h-4 w-4" />
                {sidebarExpanded && <span>Sign Out</span>}
              </Button>
            </div>

            {/* Sidebar Mode Controls */}
            <div className="mt-2 pt-3 border-t border-slate-200/50">
              <div
                className={`flex items-center ${sidebarExpanded ? 'justify-end px-2 gap-1' : 'flex-col space-y-1 justify-center'}`}
              >
                <button
                  onClick={() => setSidebarModeAndExpand('hover')}
                  className={`p-1.5 rounded-md transition-all duration-200 ${
                    sidebarMode === 'hover'
                      ? 'bg-tifpa-rose text-white shadow-sm'
                      : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100'
                  }`}
                  title="Expand on hover"
                >
                  <MousePointerClick className="h-3.5 w-3.5" />
                </button>
                <button
                  onClick={() => setSidebarModeAndExpand('expanded')}
                  className={`p-1.5 rounded-md transition-all duration-200 ${
                    sidebarMode === 'expanded'
                      ? 'bg-tifpa-rose text-white shadow-sm'
                      : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100'
                  }`}
                  title="Always expanded"
                >
                  <Maximize2 className="h-3.5 w-3.5" />
                </button>
                <button
                  onClick={() => setSidebarModeAndExpand('collapsed')}
                  className={`p-1.5 rounded-md transition-all duration-200 ${
                    sidebarMode === 'collapsed'
                      ? 'bg-tifpa-rose text-white shadow-sm'
                      : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100'
                  }`}
                  title="Always collapsed"
                >
                  <Minimize2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}
